<script setup lang="ts">
import { ref, onUnmounted, type Ref } from 'vue'
import { useI18n } from 'vue-i18n'
import type { CommonResponse } from '@/apis/api'
import { CODE_OK } from '@/apis/code'
import { handleErr } from '@/composables/exception/handler'
import type { FileDTO } from '@/composables/media/media'
import { uploadFileDetectFace, type UploadFileDetectFaceResponse } from '@/composables/portrait/portrait'
import { closeLoading, openLoading } from '@/components/business/loading/loading'
import ImageCropperPage from '@/pages/tools/ImageCropperPage.vue'
import { renderMessage } from '@/utils/util'
import { ElMessage } from 'element-plus'

const props = withDefaults(
  defineProps<{
    threeToFour?: boolean
    cropperClass?: string
  }>(),
  {
    threeToFour: true,
    cropperClass: 'fixed top-0 bottom-0 left-0 right-0 z-50 h-screen w-full'
  }
)

const emit = defineEmits<{
  success: [file: FileDTO]
}>()

const { t } = useI18n()
const showUploadImageDialog = ref(false)
const cropVisible = ref(false)
const imagePath = ref('')
const currentImageBlob = ref<Blob | null>(null)
let activeFileInput: HTMLInputElement | null = null

const revokeImagePath = () => {
  if (imagePath.value.startsWith('blob:')) {
    URL.revokeObjectURL(imagePath.value)
  }
}

const resetFlowState = (options?: { keepImagePath?: boolean }) => {
  showUploadImageDialog.value = false
  cropVisible.value = false
  currentImageBlob.value = null
  if (!options?.keepImagePath) {
    revokeImagePath()
    imagePath.value = ''
  }
}

const updateImagePath = (path: string) => {
  if (imagePath.value !== path) {
    revokeImagePath()
  }
  imagePath.value = path
}

const handleSelectedFile = (file: File | null | undefined) => {
  if (!file) {
    ElMessage({
      message: '请上传原图片，请勿剪裁',
      type: 'warning'
    })
    return
  }

  if (!/^image\/\w+/.test(file.type)) {
    ElMessage({
      message: '请上传原图片，请勿剪裁',
      type: 'warning'
    })
    return
  }

  showUploadImageDialog.value = false
  currentImageBlob.value = null
  currentImageBlob.value = file
  updateImagePath(URL.createObjectURL(file))
  cropVisible.value = true
}

const cleanupDynamicFileInput = () => {
  if (activeFileInput) {
    activeFileInput.onchange = null
    activeFileInput.remove()
    activeFileInput = null
  }
}

const selectNewImage = () => {
  cleanupDynamicFileInput()

  const input = document.createElement('input')
  input.type = 'file'
  input.accept = 'image/*'
  input.style.position = 'fixed'
  input.style.left = '-9999px'
  input.style.top = '0'
  input.style.opacity = '0'
  input.style.pointerEvents = 'none'
  input.style.width = '1px'
  input.style.height = '1px'

  input.onchange = () => {
    const file = input.files?.[0]
    handleSelectedFile(file)
    cleanupDynamicFileInput()
  }

  activeFileInput = input
  document.body.appendChild(input)
  input.click()
}


const handleUploadNewImage = () => {
  selectNewImage()
}

const handleCropImage = () => {
  if (imagePath.value) {
    showUploadImageDialog.value = false
    cropVisible.value = true
    return
  }
  selectNewImage()
}

const detectBlob = async (blob: Blob, successFile?: FileDTO) => {
  currentImageBlob.value = blob
  openLoading(t('portrait.checking_face_count'))
  try {
    const fileName = 'portrait.' + (blob.type.split('/')[1] || 'png')
    const { data, error, statusCode } = await uploadFileDetectFace({
      file: blob,
      trans: false,
      fileName
    })
    if (error.value) {
      handleErr(data, error, statusCode)
      return
    }

    const resData = data as Ref<CommonResponse<UploadFileDetectFaceResponse>>
    if (resData.value.code !== CODE_OK) {
      ElMessage({
        message: renderMessage(resData.value.message),
        type: 'warning'
      })
      return
    }

    const faceCount = resData.value.data.faceCount
    if (faceCount === 1) {
      const file = successFile || resData.value.data.file
      resetFlowState()
      emit('success', file)
      return
    }

    // if (faceCount === 0) {
    //   resetFlowState()
    //   ElMessage({
    //     message: t('portrait.upload_failed'),
    //     type: 'warning',
    //     duration: 5000
    //   })
    //   return
    // }

    showUploadImageDialog.value = true
  } finally {
    closeLoading()
  }
}

const detectImageUrl = async (url: string, successFile?: FileDTO) => {
  const timestamp = Date.now()
  let imageUrl = url
  try {
    const urlObj = new URL(url)
    urlObj.searchParams.set('t', String(timestamp))
    imageUrl = urlObj.toString()
  } catch {
    const separator = url.includes('?') ? '&' : '?'
    imageUrl = `${url}${separator}t=${timestamp}`
  }

  try {
    const response = await fetch(imageUrl, {
      mode: 'cors'
    })
    const blob = await response.blob()
    updateImagePath(URL.createObjectURL(blob))
    await detectBlob(blob, successFile)
  } catch {
    ElMessage({
      message: t('portrait.upload_failed'),
      type: 'warning',
      duration: 5000
    })
  }
}

const onCropResult = async (blob: Blob) => {
  await detectBlob(blob)
}

defineExpose({
  selectNewImage,
  detectBlob,
  detectImageUrl,
  resetFlowState
})

onUnmounted(() => {
  cleanupDynamicFileInput()
  resetFlowState()
})
</script>

<template>
  <PortraitUploadImageDialog
    v-model="showUploadImageDialog"
    @handleUploadNewImage="handleUploadNewImage"
    @handleCropImage="handleCropImage" />
  <ClientOnly>
    <teleport to="body">
      <ImageCropperPage
        v-model="cropVisible"
        :path="imagePath"
        @onCropped="onCropResult"
        :threeToFour="props.threeToFour"
        :class="props.cropperClass" />
    </teleport>
  </ClientOnly>
</template>

<style scoped></style>
